import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [""]

  connect() {
    this.messages = [
      "How's my revenue trending?",
      "How's my business performance?",
      "How's my cash burn?"
    ]

    this.currentMessageIndex = 0
    this.currentText = ""
    this.isDeleting = false
    this.typeSpeed = 80
    this.deleteSpeed = 20
    this.pauseBetweenMessages = 2000 // Pause after completing a message
    this.pauseAfterDeleting = 500 // Pause after deleting before next message
    this.pauseAfterCycle = 1000 // Pause before sliding back to center
    this.cycleCount = 0
    this.maxCycles = 1 // Complete one full cycle through all messages
    this.isAnimating = false // Add flag to prevent multiple animations

    this.cursorElement = this.element.parentElement.querySelector('.bolt-cursor')
    this.containerElement = this.element.parentElement
    this.boltActionElement = this.element.closest('.action-menu-item-bolt')

    // Listen for assistant opening instead of hardcoded delay
    this.boundHandleAssistantOpen = this.handleAssistantOpen.bind(this)
    document.addEventListener('assistant:opened', this.boundHandleAssistantOpen)
  }

  disconnect() {
    if (this.timeout) {
      clearTimeout(this.timeout)
    }
    if (this.startTimeout) {
      clearTimeout(this.startTimeout)
    }
    // Clean up event listener
    document.removeEventListener('assistant:opened', this.boundHandleAssistantOpen)
  }

  handleAssistantOpen(event) {
    // Prevent multiple animations from running simultaneously
    if (this.isAnimating) {
      return
    }

    // Clear any existing timeout
    if (this.startTimeout) {
      clearTimeout(this.startTimeout)
    }

    // Start the entrance animation with a slight delay for better UX
    this.startTimeout = setTimeout(() => {
      this.startTypewriterAnimation()
    }, 400) // Small delay after assistant opens
  }

  startTypewriterAnimation() {
    // Prevent multiple animations from running simultaneously
    if (this.isAnimating) {
      return
    }

    this.isAnimating = true

    // Add classes to trigger the slide animation
    this.boltActionElement.classList.add('typewriter-active')
    this.containerElement.classList.add('show')

    // Start typing after the slide animation completes
    setTimeout(() => {
      this.typeMessage()
    }, 400) // Match the CSS transition duration
  }

  typeMessage() {
    const currentMessage = this.messages[this.currentMessageIndex]

    if (this.isDeleting) {
      // Remove characters
      this.currentText = currentMessage.substring(0, this.currentText.length - 1)
      this.element.textContent = this.currentText

      if (this.currentText === "") {
        // Finished deleting, move to next message
        this.isDeleting = false
        this.currentMessageIndex = (this.currentMessageIndex + 1) % this.messages.length

        // Check if we've completed a full cycle
        if (this.currentMessageIndex === 0) {
          this.cycleCount++

          if (this.cycleCount >= this.maxCycles) {
            // We've completed the desired number of cycles, slide back to center
            this.timeout = setTimeout(() => {
              this.slideBackToCenter()
            }, this.pauseAfterCycle)
            return
          }
        }

        this.timeout = setTimeout(() => {
          this.typeMessage()
        }, this.pauseAfterDeleting)
      } else {
        // Continue deleting
        this.timeout = setTimeout(() => {
          this.typeMessage()
        }, this.deleteSpeed)
      }
    } else {
      // Add characters
      this.currentText = currentMessage.substring(0, this.currentText.length + 1)
      this.element.textContent = this.currentText

      if (this.currentText === currentMessage) {
        // Finished typing, pause then start deleting
        this.timeout = setTimeout(() => {
          this.isDeleting = true
          this.typeMessage()
        }, this.pauseBetweenMessages)
      } else {
        // Continue typing
        this.timeout = setTimeout(() => {
          this.typeMessage()
        }, this.typeSpeed)
      }
    }
  }

  slideBackToCenter() {
    // Remove classes to trigger the reverse slide animation
    this.containerElement.classList.remove('show')
    this.boltActionElement.classList.remove('typewriter-active')

    // Clear the text content
    this.element.textContent = ""
    this.currentText = ""

    // Reset animation flag
    this.isAnimating = false

    // Reset for potential future cycles (optional)
    // this.resetForNextCycle()
  }

  resetForNextCycle() {
    // Optional: restart the cycle after a longer pause
    setTimeout(() => {
      this.cycleCount = 0
      this.currentMessageIndex = 0
      this.isDeleting = false
      this.startTypewriterAnimation()
    }, 8000) // Wait 8 seconds before starting again
  }
} 
